// HTML to PDF Generator using html2pdf.js
// This should handle Unicode and mathematical expressions better than jsPDF

import html2pdf from 'html2pdf.js';

// Helper function to clean LaTeX expressions (enhanced version)
const cleanMathExpression = (text) => {
  if (!text) return '';
  
  // First handle special cases and complex LaTeX structures
  let cleanedText = text
    // Handle display and inline math delimiters
    .replace(/\$\$([^$]+?)\$\$/g, '$1')  // Display math
    .replace(/\$([^$]+?)\$/g, '$1')      // Inline math
    
    // Handle fractions - more comprehensive
    .replace(/\\frac\{([^}]+?)\}\{([^}]+?)\}/g, '($1)/($2)')
    .replace(/\\dfrac\{([^}]+?)\}\{([^}]+?)\}/g, '($1)/($2)')
    .replace(/\\tfrac\{([^}]+?)\}\{([^}]+?)\}/g, '($1)/($2)')
    
    // Handle square roots and nth roots
    .replace(/\\sqrt\{([^}]+?)\}/g, '√($1)')
    .replace(/\\sqrt\[(\d+)\]\{([^}]+?)\}/g, '($2)^(1/$1)')
    
    // Handle powers and subscripts - ENHANCED
    .replace(/\^{([^}]+?)}/g, (match, p1) => {
      // Handle complex superscripts
      if (p1.includes('+') || p1.includes('-') || p1.includes('*') || p1.includes('/')) {
        return `^(${p1})`;
      }
      return `^${p1}`;
    })
    .replace(/\^(\d+)/g, (match, p1) => {
      // Convert to Unicode superscripts for common numbers
      const superscripts = {
        '0': '⁰', '1': '¹', '2': '²', '3': '³', '4': '⁴',
        '5': '⁵', '6': '⁶', '7': '⁷', '8': '⁸', '9': '⁹'
      };
      if (p1.length === 1 && superscripts[p1]) {
        return superscripts[p1];
      }
      return `^${p1}`;
    })
    .replace(/\^([a-zA-Z])/g, '^$1')  // Single letter superscripts
    
    // Handle subscripts - ENHANCED
    .replace(/_{([^}]+?)}/g, (match, p1) => {
      // Handle complex subscripts
      if (p1.includes('+') || p1.includes('-') || p1.includes('*') || p1.includes('/')) {
        return `_(${p1})`;
      }
      return `_${p1}`;
    })
    .replace(/_(\d+)/g, (match, p1) => {
      // Convert to Unicode subscripts for common numbers
      const subscripts = {
        '0': '₀', '1': '₁', '2': '₂', '3': '₃', '4': '₄',
        '5': '₅', '6': '₆', '7': '₇', '8': '₈', '9': '₉'
      };
      if (p1.length === 1 && subscripts[p1]) {
        return subscripts[p1];
      }
      return `_${p1}`;
    })
    .replace(/_([a-zA-Z])/g, '_$1')  // Single letter subscripts
    
    // Handle mathematical operators
    .replace(/\\times/g, '×')
    .replace(/\\cdot/g, '·')
    .replace(/\\div/g, '÷')
    .replace(/\\pm/g, '±')
    .replace(/\\mp/g, '∓')
    
    // Handle inequalities
    .replace(/\\leq/g, '≤')
    .replace(/\\geq/g, '≥')
    .replace(/\\neq/g, '≠')
    .replace(/\\approx/g, '≈')
    .replace(/\\equiv/g, '≡')
    .replace(/\\sim/g, '~')
    .replace(/\\propto/g, '∝')
    
    // Handle sets and logic
    .replace(/\\in/g, '∈')
    .replace(/\\notin/g, '∉')
    .replace(/\\subset/g, '⊂')
    .replace(/\\supset/g, '⊃')
    .replace(/\\subseteq/g, '⊆')
    .replace(/\\supseteq/g, '⊇')
    .replace(/\\cup/g, '∪')
    .replace(/\\cap/g, '∩')
    .replace(/\\emptyset/g, '∅')
    .replace(/\\varnothing/g, '∅')
    .replace(/\\mathbb\{R\}/g, 'ℝ')
    .replace(/\\mathbb\{N\}/g, 'ℕ')
    .replace(/\\mathbb\{Z\}/g, 'ℤ')
    .replace(/\\mathbb\{Q\}/g, 'ℚ')
    .replace(/\\mathbb\{C\}/g, 'ℂ')
    
    // Handle infinity and limits
    .replace(/\\infty/g, '∞')
    .replace(/\\to/g, '→')
    .replace(/\\rightarrow/g, '→')
    .replace(/\\leftarrow/g, '←')
    .replace(/\\leftrightarrow/g, '↔')
    .replace(/\\Rightarrow/g, '⇒')
    .replace(/\\Leftarrow/g, '⇐')
    .replace(/\\Leftrightarrow/g, '⇔')
    
    // Handle Greek letters - COMPREHENSIVE
    .replace(/\\alpha/g, 'α')
    .replace(/\\beta/g, 'β')
    .replace(/\\gamma/g, 'γ')
    .replace(/\\Gamma/g, 'Γ')
    .replace(/\\delta/g, 'δ')
    .replace(/\\Delta/g, 'Δ')
    .replace(/\\epsilon/g, 'ε')
    .replace(/\\varepsilon/g, 'ε')
    .replace(/\\zeta/g, 'ζ')
    .replace(/\\eta/g, 'η')
    .replace(/\\theta/g, 'θ')
    .replace(/\\Theta/g, 'Θ')
    .replace(/\\vartheta/g, 'ϑ')
    .replace(/\\iota/g, 'ι')
    .replace(/\\kappa/g, 'κ')
    .replace(/\\lambda/g, 'λ')
    .replace(/\\Lambda/g, 'Λ')
    .replace(/\\mu/g, 'μ')
    .replace(/\\nu/g, 'ν')
    .replace(/\\xi/g, 'ξ')
    .replace(/\\Xi/g, 'Ξ')
    .replace(/\\pi/g, 'π')
    .replace(/\\Pi/g, 'Π')
    .replace(/\\varpi/g, 'ϖ')
    .replace(/\\rho/g, 'ρ')
    .replace(/\\varrho/g, 'ϱ')
    .replace(/\\sigma/g, 'σ')
    .replace(/\\Sigma/g, 'Σ')
    .replace(/\\varsigma/g, 'ς')
    .replace(/\\tau/g, 'τ')
    .replace(/\\upsilon/g, 'υ')
    .replace(/\\Upsilon/g, 'Υ')
    .replace(/\\phi/g, 'φ')
    .replace(/\\Phi/g, 'Φ')
    .replace(/\\varphi/g, 'ϕ')
    .replace(/\\chi/g, 'χ')
    .replace(/\\psi/g, 'ψ')
    .replace(/\\Psi/g, 'Ψ')
    .replace(/\\omega/g, 'ω')
    .replace(/\\Omega/g, 'Ω')
    
    // Handle trigonometric functions
    .replace(/\\sin/g, 'sin')
    .replace(/\\cos/g, 'cos')
    .replace(/\\tan/g, 'tan')
    .replace(/\\cot/g, 'cot')
    .replace(/\\sec/g, 'sec')
    .replace(/\\csc/g, 'csc')
    .replace(/\\arcsin/g, 'arcsin')
    .replace(/\\arccos/g, 'arccos')
    .replace(/\\arctan/g, 'arctan')
    .replace(/\\sinh/g, 'sinh')
    .replace(/\\cosh/g, 'cosh')
    .replace(/\\tanh/g, 'tanh')
    
    // Handle logarithms
    .replace(/\\log/g, 'log')
    .replace(/\\ln/g, 'ln')
    .replace(/\\lg/g, 'lg')
    
    // Handle limits and calculus
    .replace(/\\lim/g, 'lim')
    .replace(/\\sup/g, 'sup')
    .replace(/\\inf/g, 'inf')
    .replace(/\\max/g, 'max')
    .replace(/\\min/g, 'min')
    
    // Handle system of equations - IMPORTANT FIX
    .replace(/\\begin\{cases\}/g, '')
    .replace(/\\end\{cases\}/g, '')
    .replace(/\\\\/g, '<br/>')  // Convert LaTeX line breaks to HTML
    
    // Handle matrices and arrays
    .replace(/\\begin\{matrix\}/g, '')
    .replace(/\\end\{matrix\}/g, '')
    .replace(/\\begin\{pmatrix\}/g, '(')
    .replace(/\\end\{pmatrix\}/g, ')')
    .replace(/\\begin\{bmatrix\}/g, '[')
    .replace(/\\end\{bmatrix\}/g, ']')
    .replace(/\\begin\{vmatrix\}/g, '|')
    .replace(/\\end\{vmatrix\}/g, '|')
    
    // Handle parentheses and brackets
    .replace(/\\left\(/g, '(')
    .replace(/\\right\)/g, ')')
    .replace(/\\left\[/g, '[')
    .replace(/\\right\]/g, ']')
    .replace(/\\left\{/g, '{')
    .replace(/\\right\}/g, '}')
    .replace(/\\left\|/g, '|')
    .replace(/\\right\|/g, '|')
    
    // Handle spacing
    .replace(/\\,/g, ' ')
    .replace(/\\;/g, ' ')
    .replace(/\\:/g, ' ')
    .replace(/\\!/g, '')
    .replace(/\\ /g, ' ')
    .replace(/\\quad/g, '    ')  // Convert to regular spaces for PDF
    .replace(/\\qquad/g, '        ')
    
    // Handle line breaks
    .replace(/\\\\n/g, '\n')
    .replace(/\\n/g, '\n')
    
    // Handle special symbols
    .replace(/\\degree/g, '°')
    .replace(/\\celsius/g, '℃')
    .replace(/\\fahrenheit/g, '℉')
    .replace(/\\partial/g, '∂')
    .replace(/\\nabla/g, '∇')
    .replace(/\\forall/g, '∀')
    .replace(/\\exists/g, '∃')
    .replace(/\\nexists/g, '∄')
    .replace(/\\therefore/g, '∴')
    .replace(/\\because/g, '∵')
    .replace(/\\angle/g, '∠')
    .replace(/\\perp/g, '⊥')
    .replace(/\\parallel/g, '∥')
    
    // Clean up remaining backslashes and braces - MORE AGGRESSIVE
    .replace(/\\([a-zA-Z]+)/g, '$1')  // Remove backslashes from remaining commands
    .replace(/\{([^}]*)\}/g, '$1')    // Remove remaining braces
    
    // Final cleanup
    .replace(/\s+/g, ' ')  // Multiple spaces to single space
    .trim();

  return cleanedText;
};

// Additional post-processing for stubborn LaTeX expressions
const postProcessMathText = (text) => {
  if (!text) return '';
  
  return text
    // Handle remaining superscripts that might have been missed
    .replace(/([a-zA-Z0-9])\^2/g, '$1²')
    .replace(/([a-zA-Z0-9])\^3/g, '$1³')
    .replace(/([a-zA-Z0-9])\^4/g, '$1⁴')
    .replace(/([a-zA-Z0-9])\^5/g, '$1⁵')
    .replace(/([a-zA-Z0-9])\^6/g, '$1⁶')
    .replace(/([a-zA-Z0-9])\^7/g, '$1⁷')
    .replace(/([a-zA-Z0-9])\^8/g, '$1⁸')
    .replace(/([a-zA-Z0-9])\^9/g, '$1⁹')
    .replace(/([a-zA-Z0-9])\^0/g, '$1⁰')
    .replace(/([a-zA-Z0-9])\^1/g, '$1¹')
    
    // Handle parenthetical superscripts
    .replace(/\(([^)]+)\)\^2/g, '($1)²')
    .replace(/\(([^)]+)\)\^3/g, '($1)³')
    
    // Handle specific expressions from the PDF
    .replace(/x\^2/g, 'x²')
    .replace(/y\^2/g, 'y²')
    .replace(/s\^2/g, 's²')
    .replace(/p\^2/g, 'p²')
    .replace(/\(x\+y\)\^2/g, '(x+y)²')
    .replace(/\(x-y\)\^2/g, '(x-y)²')
    .replace(/\(5-x\)\^2/g, '(5-x)²')
    .replace(/\(t-2\)\^2/g, '(t-2)²')
    .replace(/\(t-3\)\^2/g, '(t-3)²')
    
    // Handle mathematical expressions with multiplication
    .replace(/2x\^2/g, '2x²')
    .replace(/3x\^2/g, '3x²')
    .replace(/4x\^2/g, '4x²')
    .replace(/5x\^2/g, '5x²')
    .replace(/10x/g, '10x')
    .replace(/2xy/g, '2xy')
    .replace(/3xy/g, '3xy')
    
    // Handle common equation patterns
    .replace(/= 0\$/g, '= 0')
    .replace(/= 13\$/g, '= 13')
    .replace(/= 37\$/g, '= 37')
    .replace(/= 49\$/g, '= 49')
    .replace(/= 35\$/g, '= 35')
    .replace(/= 25\$/g, '= 25')
    
    // Clean up any remaining dollar signs
    .replace(/\$/g, '')
    
    // Handle coordinate notation
    .replace(/\$\(x,y\)/g, '(x,y)')
    .replace(/\(x,y\) = \(/g, '(x,y) = (')
    
    // Handle factorization patterns
    .replace(/\$\(([^)]+)\)\(([^)]+)\)/g, '($1)($2)')
    
    // Handle remaining common patterns
    .replace(/\$x = /g, 'x = ')
    .replace(/\$y = /g, 'y = ')
    .replace(/\$s = /g, 's = ')
    .replace(/\$p = /g, 'p = ')
    
    .trim();
};

// Process all text content in question data to clean LaTeX
const processQuestionData = (questionData) => {
  const processedData = JSON.parse(JSON.stringify(questionData)); // Deep copy
  
  // Clean main question
  if (processedData.question) {
    processedData.question = postProcessMathText(cleanMathExpression(processedData.question));
  }
  
  // Clean multi-part questions
  if (processedData.parts) {
    Object.keys(processedData.parts).forEach(partKey => {
      const part = processedData.parts[partKey];
      
      // Handle M12 format where parts[partKey] is directly a string
      if (typeof part === 'string') {
        // Convert M12 format to expected object format
        processedData.parts[partKey] = {
          question: postProcessMathText(cleanMathExpression(part)),
          hint: processedData.hint?.[partKey] ? postProcessMathText(cleanMathExpression(processedData.hint[partKey])) : null,
          solution: processedData.solution?.[partKey] ? postProcessMathText(cleanMathExpression(processedData.solution[partKey])) : null
        };
      } else {
        // Handle other formats where part is an object
        if (part.question) {
          part.question = postProcessMathText(cleanMathExpression(part.question));
        }
        if (part.hint) {
          part.hint = postProcessMathText(cleanMathExpression(part.hint));
        }
        if (part.solution) {
          if (part.solution.steps) {
            part.solution.steps = part.solution.steps.map(step => 
              postProcessMathText(cleanMathExpression(step))
            );
          }
          if (part.solution.answer) {
            part.solution.answer = postProcessMathText(cleanMathExpression(part.solution.answer));
          }
        }
      }
    });
  }
  
  return processedData;
};

// Generate PDF from HTML content
const generatePDFFromHTML = (htmlContent, filename) => {
  const options = {
    margin: 1,
    filename: filename,
    image: { type: 'jpeg', quality: 0.98 },
    html2canvas: { 
      scale: 2,
      useCORS: true,
      allowTaint: true,
      letterRendering: true
    },
    jsPDF: { 
      unit: 'in', 
      format: 'a4', 
      orientation: 'portrait',
      compress: true
    }
  };

  return html2pdf().set(options).from(htmlContent).save();
};

// Create HTML content for a single question
const createQuestionHTML = (questionData, topicInfo, questionNumber, moduleInfo) => {
  // Process the question data to clean LaTeX expressions
  const processedData = processQuestionData(questionData);
  
  const title = `${moduleInfo?.name || '數學練習'} - ${topicInfo?.name || '練習題'}`;
  
  let questionContent = '';
  
  // Main question
  if (processedData.question) {
    questionContent += `
      <div class="question-main">
        <h3>問題 ${questionNumber}:</h3>
        <div class="question-text">${processedData.question}</div>
      </div>
    `;
  }

  // Multi-part questions
  if (processedData.parts) {
    questionContent += '<div class="multi-part-questions">';
    ['a', 'b', 'c'].forEach(partKey => {
      if (processedData.parts[partKey]) {
        const part = processedData.parts[partKey];
        questionContent += `
          <div class="question-part">
            <div class="part-label">(${partKey})</div>
            <div class="part-question">${part.question || ''}</div>
            ${part.hint ? `<div class="part-hint"><strong>提示:</strong> ${part.hint}</div>` : ''}
          </div>
        `;
      }
    });
    questionContent += '</div>';
  }

  // Answer space
  questionContent += `
    <div class="answer-space">
      <h4>答案:</h4>
      <div class="answer-lines">
        ${'<div class="answer-line"></div>'.repeat(5)}
      </div>
    </div>
  `;

  return `
    <!DOCTYPE html>
    <html lang="zh-TW">
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>${title}</title>
      <style>
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        
        body {
          font-family: 'Microsoft YaHei', 'PingFang SC', 'Hiragino Sans GB', 'SimHei', Arial, sans-serif;
          line-height: 1.6;
          color: #333;
          padding: 20px;
          background: white;
        }
        
        .header {
          text-align: center;
          margin-bottom: 30px;
          padding-bottom: 20px;
          border-bottom: 2px solid #4F46E5;
        }
        
        .header h1 {
          color: #4F46E5;
          font-size: 24px;
          margin-bottom: 10px;
        }
        
        .header .subtitle {
          color: #666;
          font-size: 14px;
        }
        
        .question-main {
          margin-bottom: 20px;
        }
        
        .question-main h3 {
          color: #1F2937;
          font-size: 18px;
          margin-bottom: 10px;
        }
        
        .question-text {
          font-size: 16px;
          line-height: 1.8;
          margin-bottom: 15px;
        }
        
        .multi-part-questions {
          margin: 20px 0;
        }
        
        .question-part {
          margin: 15px 0;
          padding: 15px;
          border: 1px solid #E5E7EB;
          border-radius: 8px;
          background: #F9FAFB;
        }
        
        .part-label {
          display: inline-block;
          background: #3B82F6;
          color: white;
          padding: 4px 8px;
          border-radius: 4px;
          font-weight: bold;
          font-size: 14px;
          margin-bottom: 8px;
        }
        
        .part-question {
          font-size: 15px;
          line-height: 1.7;
          margin-bottom: 10px;
          font-family: 'Times New Roman', 'Microsoft YaHei', serif;
        }
        
        .part-hint {
          background: #FEF3C7;
          border: 1px solid #F59E0B;
          border-radius: 4px;
          padding: 8px 12px;
          font-size: 13px;
          color: #92400E;
        }
        
        .answer-space {
          margin-top: 30px;
          padding: 20px;
          border: 1px solid #D1D5DB;
          border-radius: 8px;
          background: #F9FAFB;
        }
        
        .answer-space h4 {
          color: #374151;
          margin-bottom: 15px;
          font-size: 16px;
        }
        
        .answer-line {
          height: 30px;
          border-bottom: 1px solid #9CA3AF;
          margin-bottom: 15px;
        }
        
        .footer {
          margin-top: 40px;
          text-align: center;
          font-size: 12px;
          color: #6B7280;
          border-top: 1px solid #E5E7EB;
          padding-top: 15px;
        }
        
        /* Mathematical expressions styling */
        .math {
          font-family: 'Times New Roman', 'Microsoft YaHei', serif;
          font-weight: normal;
        }
        
        /* System of equations styling */
        .equation-system {
          font-family: 'Times New Roman', serif;
          font-size: 16px;
          line-height: 1.5;
          margin: 10px 0;
          padding: 10px;
          background: #F8F9FA;
          border-left: 4px solid #3B82F6;
        }
        
        /* Print-friendly styles */
        @media print {
          body {
            padding: 15px;
          }
          
          .header {
            margin-bottom: 20px;
          }
          
          .question-part {
            break-inside: avoid;
            page-break-inside: avoid;
          }
        }
      </style>
    </head>
    <body>
      <div class="header">
        <h1>${title}</h1>
        <div class="subtitle">問題 ${questionNumber} | ${processedData.difficulty ? `難度: ${processedData.difficulty}` : ''}</div>
      </div>
      
      ${questionContent}
      
      <div class="footer">
        生成時間: ${new Date().toLocaleString('zh-TW')} | RightMaths 數學學習系統
      </div>
    </body>
    </html>
  `;
};

// Create HTML content for all questions in a topic
const createTopicHTML = (problems, topicInfo, moduleInfo) => {
  const title = `${moduleInfo?.name || '數學練習'} - ${topicInfo?.name || '練習題集'}`;
  
  let questionsContent = '';
  
  problems.forEach((questionData, index) => {
    // Process each question data to clean LaTeX expressions
    const processedData = processQuestionData(questionData);
    const questionNumber = index + 1;
    
    questionsContent += `
      <div class="question-container">
        <div class="question-header">
          <h3>問題 ${questionNumber}</h3>
          ${processedData.difficulty ? `<span class="difficulty-badge difficulty-${processedData.difficulty}">${processedData.difficulty}</span>` : ''}
        </div>
    `;
    
    // Main question
    if (processedData.question) {
      questionsContent += `
        <div class="question-text">${processedData.question}</div>
      `;
    }

    // Multi-part questions
    if (processedData.parts) {
      questionsContent += '<div class="multi-part-questions">';
      ['a', 'b', 'c'].forEach(partKey => {
        if (processedData.parts[partKey]) {
          const part = processedData.parts[partKey];
          questionsContent += `
            <div class="question-part">
              <div class="part-label">(${partKey})</div>
              <div class="part-question">${part.question || ''}</div>
            </div>
          `;
        }
      });
      questionsContent += '</div>';
    }

    // Answer space (smaller for topic PDF)
    questionsContent += `
      <div class="answer-space-small">
        <div class="answer-lines-small">
          ${'<div class="answer-line-small"></div>'.repeat(2)}
        </div>
      </div>
      </div>
    `;
    
    // Add page break after every 3 questions
    if ((index + 1) % 3 === 0 && index < problems.length - 1) {
      questionsContent += '<div class="page-break"></div>';
    }
  });

  return `
    <!DOCTYPE html>
    <html lang="zh-TW">
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>${title}</title>
      <style>
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        
        body {
          font-family: 'Microsoft YaHei', 'PingFang SC', 'Hiragino Sans GB', 'SimHei', Arial, sans-serif;
          line-height: 1.5;
          color: #333;
          padding: 20px;
          background: white;
        }
        
        .header {
          text-align: center;
          margin-bottom: 30px;
          padding-bottom: 20px;
          border-bottom: 2px solid #4F46E5;
        }
        
        .header h1 {
          color: #4F46E5;
          font-size: 22px;
          margin-bottom: 8px;
        }
        
        .header .subtitle {
          color: #666;
          font-size: 14px;
        }
        
        .question-container {
          margin-bottom: 25px;
          padding: 15px;
          border: 1px solid #E5E7EB;
          border-radius: 8px;
          background: #FAFAFA;
          break-inside: avoid;
          page-break-inside: avoid;
        }
        
        .question-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
        }
        
        .question-header h3 {
          color: #1F2937;
          font-size: 16px;
        }
        
        .difficulty-badge {
          font-size: 11px;
          padding: 2px 6px;
          border-radius: 4px;
          color: white;
          font-weight: bold;
        }
        
        .difficulty-basic { background: #10B981; }
        .difficulty-intermediate { background: #F59E0B; }
        .difficulty-advanced { background: #EF4444; }
        
        .question-text {
          font-size: 14px;
          line-height: 1.6;
          margin-bottom: 12px;
        }
        
        .multi-part-questions {
          margin: 12px 0;
        }
        
        .question-part {
          margin: 10px 0;
          padding: 10px;
          border: 1px solid #D1D5DB;
          border-radius: 6px;
          background: white;
        }
        
        .part-label {
          display: inline-block;
          background: #3B82F6;
          color: white;
          padding: 2px 6px;
          border-radius: 3px;
          font-weight: bold;
          font-size: 12px;
          margin-bottom: 6px;
        }
        
        .part-question {
          font-size: 13px;
          line-height: 1.5;
          font-family: 'Times New Roman', 'Microsoft YaHei', serif;
        }
        
        .answer-space-small {
          margin-top: 15px;
          padding: 10px;
          border-top: 1px solid #D1D5DB;
          background: white;
        }
        
        .answer-line-small {
          height: 20px;
          border-bottom: 1px solid #9CA3AF;
          margin-bottom: 8px;
        }
        
        .page-break {
          page-break-after: always;
        }
        
        .footer {
          margin-top: 30px;
          text-align: center;
          font-size: 11px;
          color: #6B7280;
          border-top: 1px solid #E5E7EB;
          padding-top: 15px;
        }
        
        /* Mathematical expressions styling */
        .math {
          font-family: 'Times New Roman', 'Microsoft YaHei', serif;
          font-weight: normal;
        }
        
        /* Print-friendly styles */
        @media print {
          body {
            padding: 15px;
          }
          
          .question-container {
            break-inside: avoid;
            page-break-inside: avoid;
          }
        }
      </style>
    </head>
    <body>
      <div class="header">
        <h1>${title}</h1>
        <div class="subtitle">共 ${problems.length} 題 | 生成時間: ${new Date().toLocaleString('zh-TW')}</div>
      </div>
      
      ${questionsContent}
      
      <div class="footer">
        RightMaths 數學學習系統 | 共 ${problems.length} 題
      </div>
    </body>
    </html>
  `;
};

// Generate PDF for a single question using HTML2PDF
export const generateQuestionHTMLPDF = (questionData, topicInfo, questionNumber, moduleInfo) => {
  try {
    const htmlContent = createQuestionHTML(questionData, topicInfo, questionNumber, moduleInfo);
    const filename = `${moduleInfo?.id || 'Math'}_${topicInfo?.id || 'Topic'}_Q${questionNumber}_HTML.pdf`;
    
    return generatePDFFromHTML(htmlContent, filename);
  } catch (error) {
    console.error('Error generating HTML PDF:', error);
    throw error;
  }
};

// Generate PDF for all questions in a topic using HTML2PDF
export const generateTopicHTMLPDF = (problems, topicInfo, moduleInfo) => {
  try {
    const htmlContent = createTopicHTML(problems, topicInfo, moduleInfo);
    const filename = `${moduleInfo?.id || 'Math'}_${topicInfo?.id || 'Topic'}_全部題目_HTML.pdf`;
    
    return generatePDFFromHTML(htmlContent, filename);
  } catch (error) {
    console.error('Error generating topic HTML PDF:', error);
    throw error;
  }
};

// Generate PDF with solutions using HTML2PDF
export const generateQuestionWithSolutionHTMLPDF = (questionData, topicInfo, questionNumber, moduleInfo) => {
  try {
    // Process the question data to clean LaTeX expressions
    const processedData = processQuestionData(questionData);
    
    // Enhanced HTML with solutions
    let htmlContent = createQuestionHTML(processedData, topicInfo, questionNumber, moduleInfo);
    
    // Add solutions section if available
    if (processedData.parts) {
      let solutionsHTML = '<div class="solutions-section"><h3>詳細解答:</h3>';
      
      ['a', 'b', 'c'].forEach(partKey => {
        if (processedData.parts[partKey] && processedData.parts[partKey].solution) {
          const part = processedData.parts[partKey];
          const solution = part.solution;
          
          solutionsHTML += `
            <div class="solution-part">
              <div class="solution-label">(${partKey}) 解答:</div>
              ${typeof solution === 'string' ? `
                <div class="solution-content">
                  ${solution.split('\\n').map(line => `<p>${line}</p>`).join('')}
                </div>
              ` : `
                ${solution.steps ? `
                  <div class="solution-steps">
                    <ol>
                      ${solution.steps.map(step => `<li>${step}</li>`).join('')}
                    </ol>
                  </div>
                ` : ''}
                ${solution.answer ? `
                  <div class="final-answer">
                    <strong>答案:</strong> ${solution.answer}
                  </div>
                ` : ''}
              `}
            </div>
          `;
        }
      });
      
      solutionsHTML += '</div>';
      
      // Add CSS for solutions section
      const solutionsCSS = `
        <style>
          .solutions-section {
            margin-top: 30px;
            padding: 20px;
            border: 2px solid #10B981;
            border-radius: 8px;
            background: #F0FDF4;
          }
          
          .solutions-section h3 {
            color: #065F46;
            margin-bottom: 20px;
            font-size: 18px;
          }
          
          .solution-part {
            margin: 15px 0;
            padding: 15px;
            background: white;
            border: 1px solid #10B981;
            border-radius: 6px;
          }
          
          .solution-label {
            font-weight: bold;
            color: #065F46;
            margin-bottom: 10px;
            font-size: 14px;
          }
          
          .solution-steps ol {
            margin: 10px 0;
            padding-left: 20px;
          }
          
          .solution-steps li {
            margin: 5px 0;
            line-height: 1.5;
            font-family: 'Times New Roman', 'Microsoft YaHei', serif;
          }
          
          .final-answer {
            margin-top: 10px;
            padding: 8px 12px;
            background: #DCFCE7;
            border: 1px solid #10B981;
            border-radius: 4px;
            font-family: 'Times New Roman', 'Microsoft YaHei', serif;
          }
          
          .solution-content {
            margin: 10px 0;
            padding: 8px 12px;
            background: #F8F9FA;
            border: 1px solid #E5E7EB;
            border-radius: 4px;
          }
          
          .solution-content p {
            margin: 5px 0;
            line-height: 1.5;
            font-family: 'Times New Roman', 'Microsoft YaHei', serif;
          }
        </style>
      `;
      
      // Insert solutions and CSS before footer
      htmlContent = htmlContent.replace(
        '</head>',
        solutionsCSS + '</head>'
      ).replace(
        '<div class="footer">',
        solutionsHTML + '<div class="footer">'
      );
    }
    
    const filename = `${moduleInfo?.id || 'Math'}_${topicInfo?.id || 'Topic'}_Q${questionNumber}_含解答_HTML.pdf`;
    
    return generatePDFFromHTML(htmlContent, filename);
  } catch (error) {
    console.error('Error generating solution HTML PDF:', error);
    throw error;
  }
}; 

// Generate PDF for all questions in a topic WITH solutions using HTML2PDF
export const generateTopicWithSolutionsHTMLPDF = (problems, topicInfo, moduleInfo) => {
  try {
    // Process all problems to clean LaTeX expressions
    const processedProblems = problems.map(problem => processQuestionData(problem));
    
    const title = `${moduleInfo?.name || '數學練習'} - ${topicInfo?.name || '練習題集'} (含詳細解答)`;
    
    let questionsContent = '';
    
    processedProblems.forEach((processedData, index) => {
      const questionNumber = index + 1;
      
      questionsContent += `
        <div class="question-container-with-solution">
          <div class="question-header">
            <h3>問題 ${questionNumber}</h3>
            ${processedData.difficulty ? `<span class="difficulty-badge difficulty-${processedData.difficulty}">${processedData.difficulty}</span>` : ''}
          </div>
      `;
      
      // Main question
      if (processedData.question) {
        questionsContent += `
          <div class="question-text">${processedData.question}</div>
        `;
      }

      // Multi-part questions
      if (processedData.parts) {
        questionsContent += '<div class="multi-part-questions">';
        ['a', 'b', 'c'].forEach(partKey => {
          if (processedData.parts[partKey]) {
            const part = processedData.parts[partKey];
            questionsContent += `
              <div class="question-part">
                <div class="part-label">(${partKey})</div>
                <div class="part-question">${part.question || ''}</div>
                ${part.hint ? `<div class="part-hint"><strong>提示:</strong> ${part.hint}</div>` : ''}
              </div>
            `;
          }
        });
        questionsContent += '</div>';
      }

      // Add solutions section if available
      if (processedData.parts) {
        questionsContent += '<div class="solutions-section-compact">';
        questionsContent += '<h4>詳細解答:</h4>';
        
        ['a', 'b', 'c'].forEach(partKey => {
          if (processedData.parts[partKey] && processedData.parts[partKey].solution) {
            const part = processedData.parts[partKey];
            const solution = part.solution;
            
            questionsContent += `
              <div class="solution-part-compact">
                <div class="solution-label">(${partKey}) 解答:</div>
                ${typeof solution === 'string' ? `
                  <div class="solution-content-compact">
                    ${solution.split('\\n').map(line => `<p>${line}</p>`).join('')}
                  </div>
                ` : `
                  ${solution.steps ? `
                    <div class="solution-steps-compact">
                      <ol>
                        ${solution.steps.map(step => `<li>${step}</li>`).join('')}
                      </ol>
                    </div>
                  ` : ''}
                  ${solution.answer ? `
                    <div class="final-answer-compact">
                      <strong>答案:</strong> ${solution.answer}
                    </div>
                  ` : ''}
                `}
              </div>
            `;
          }
        });
        
        questionsContent += '</div>';
      }

      questionsContent += '</div>'; // Close question-container-with-solution
      
      // Add page break after every 2 questions (since we have solutions now)
      if ((index + 1) % 2 === 0 && index < processedProblems.length - 1) {
        questionsContent += '<div class="page-break"></div>';
      }
    });

    const htmlContent = `
      <!DOCTYPE html>
      <html lang="zh-TW">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>${title}</title>
        <style>
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          
          body {
            font-family: 'Microsoft YaHei', 'PingFang SC', 'Hiragino Sans GB', 'SimHei', Arial, sans-serif;
            line-height: 1.4;
            color: #333;
            padding: 15px;
            background: white;
          }
          
          .header {
            text-align: center;
            margin-bottom: 25px;
            padding-bottom: 15px;
            border-bottom: 2px solid #4F46E5;
          }
          
          .header h1 {
            color: #4F46E5;
            font-size: 20px;
            margin-bottom: 6px;
          }
          
          .header .subtitle {
            color: #666;
            font-size: 12px;
          }
          
          .question-container-with-solution {
            margin-bottom: 30px;
            padding: 15px;
            border: 1px solid #E5E7EB;
            border-radius: 8px;
            background: #FAFAFA;
            break-inside: avoid;
            page-break-inside: avoid;
          }
          
          .question-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 12px;
            padding-bottom: 8px;
            border-bottom: 1px solid #D1D5DB;
          }
          
          .question-header h3 {
            color: #1F2937;
            font-size: 16px;
          }
          
          .difficulty-badge {
            font-size: 10px;
            padding: 2px 6px;
            border-radius: 4px;
            color: white;
            font-weight: bold;
          }
          
          .difficulty-basic { background: #10B981; }
          .difficulty-intermediate { background: #F59E0B; }
          .difficulty-advanced { background: #EF4444; }
          
          .question-text {
            font-size: 14px;
            line-height: 1.5;
            margin-bottom: 12px;
            font-weight: 500;
          }
          
          .multi-part-questions {
            margin: 12px 0;
          }
          
          .question-part {
            margin: 8px 0;
            padding: 8px;
            border: 1px solid #D1D5DB;
            border-radius: 6px;
            background: white;
          }
          
          .part-label {
            display: inline-block;
            background: #3B82F6;
            color: white;
            padding: 2px 6px;
            border-radius: 3px;
            font-weight: bold;
            font-size: 11px;
            margin-bottom: 4px;
          }
          
          .part-question {
            font-size: 12px;
            line-height: 1.4;
            font-family: 'Times New Roman', 'Microsoft YaHei', serif;
          }
          
          .part-hint {
            margin-top: 6px;
            padding: 4px 8px;
            background: #FEF3C7;
            border: 1px solid #F59E0B;
            border-radius: 4px;
            font-size: 11px;
            color: #92400E;
          }
          
          .solutions-section-compact {
            margin-top: 15px;
            padding: 12px;
            border: 1px solid #10B981;
            border-radius: 6px;
            background: #F0FDF4;
          }
          
          .solutions-section-compact h4 {
            color: #065F46;
            margin-bottom: 10px;
            font-size: 14px;
          }
          
          .solution-part-compact {
            margin: 8px 0;
            padding: 8px;
            background: white;
            border: 1px solid #A7F3D0;
            border-radius: 4px;
          }
          
          .solution-label {
            font-weight: bold;
            color: #065F46;
            margin-bottom: 6px;
            font-size: 12px;
          }
          
          .solution-steps-compact ol {
            margin: 6px 0;
            padding-left: 16px;
          }
          
          .solution-steps-compact li {
            margin: 3px 0;
            line-height: 1.3;
            font-size: 11px;
            font-family: 'Times New Roman', 'Microsoft YaHei', serif;
          }
          
          .final-answer-compact {
            margin-top: 6px;
            padding: 4px 8px;
            background: #DCFCE7;
            border: 1px solid #10B981;
            border-radius: 3px;
            font-size: 11px;
            font-family: 'Times New Roman', 'Microsoft YaHei', serif;
          }
          
          .solution-content-compact {
            margin: 6px 0;
            padding: 4px 8px;
            background: #F8F9FA;
            border: 1px solid #E5E7EB;
            border-radius: 3px;
          }
          
          .solution-content-compact p {
            margin: 3px 0;
            line-height: 1.3;
            font-size: 11px;
            font-family: 'Times New Roman', 'Microsoft YaHei', serif;
          }
          
          .page-break {
            page-break-after: always;
          }
          
          .footer {
            margin-top: 25px;
            text-align: center;
            font-size: 10px;
            color: #6B7280;
            border-top: 1px solid #E5E7EB;
            padding-top: 12px;
          }
          
          /* Mathematical expressions styling */
          .math {
            font-family: 'Times New Roman', 'Microsoft YaHei', serif;
            font-weight: normal;
          }
          
          /* Print-friendly styles */
          @media print {
            body {
              padding: 12px;
            }
            
            .question-container-with-solution {
              break-inside: avoid;
              page-break-inside: avoid;
            }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>${title}</h1>
          <div class="subtitle">共 ${processedProblems.length} 題 (含詳細解答) | 生成時間: ${new Date().toLocaleString('zh-TW')}</div>
        </div>
        
        ${questionsContent}
        
        <div class="footer">
          RightMaths 數學學習系統 | 共 ${processedProblems.length} 題 (含詳細解答)
        </div>
      </body>
      </html>
    `;
    
    const filename = `${moduleInfo?.id || 'Math'}_${topicInfo?.id || 'Topic'}_全部題目含解答_HTML.pdf`;
    
    return generatePDFFromHTML(htmlContent, filename);
  } catch (error) {
    console.error('Error generating topic with solutions HTML PDF:', error);
    throw error;
  }
}; 